import type {
  FilterParamsHotels,
  FilterParamsOffers,
  SearchParams,
  SortingOption,
  SortParams,
} from '@/modules/common/context/QueryParams/models';
import type {RoomType} from '@/modules/common/models/room';
import type {IStringifyOptions} from 'qs';

import {differenceInDays, parseISO} from 'date-fns';
import {mapValues} from 'lodash';
import {stringify as qsStringify} from 'qs';

import {config} from '@/config';
import {
  REVIEW_SCORE_AMADEUS_MAX,
  REVIEW_SCORE_AMADEUS_MAX_BACKEND,
} from '@/consts';
import {TravelTypes} from '@/modules/common/context/QueryParams/models';
import {getRatingCategoryThreshold} from '@/modules/common/utils/rating';
import {BFF_RATING_TYPE_AMADEUS} from '@/redux/v1/models';
import {formatMinutesAsTime} from '@/utils/date';

type SearchParamsBackend = {
  destinationId?: null | string;
  dateMin?: null | string;
  dateMax?: null | string;
  airports?: null | Array<string>;
  // @FIXME can this be typed somehow?
  // rooms[number][adults]: number;
  // rooms[number][children][number]: number;
  // rooms[number][roomType][number]: string;
  // rooms[number][boardType][number]: string;
  durationMin?: null | number;
  durationMax?: null | number;
  travelType?: null | string;
};

type FilterParamsBackend = {
  priceMin?: number | null;
  priceMax?: number | null;
  hotelAttributes?: Array<string> | null;
  roomTypes?: Array<string> | null;
  roomAttributes?: Array<string> | null;
  boardTypes?: Array<string> | null;
  ratingMin?: number | null;
  categoryMin?: number | null;
  tourOperators?: Array<string> | null;
  transportations?: Array<string> | null;
  durationFilter?: number | null;
  departureTimeMin?: string | null;
  departureTimeMax?: string | null;
  returnTimeMin?: string | null;
  returnTimeMax?: string | null;
  specials?: Array<string> | null;
  destinationIdFilter?: string | null;
  airlines?: Array<string> | null;
  arrivalAirports?: Array<string> | null;
  distanceAirportMax?: number | null;
  distanceBeachMax?: number | null;
  distanceCityCenterMax?: number | null;
  distanceSkiSlopeMax?: number | null;
};

type SortParamsBackend = {
  sortBy: SortingOption;
};

// overwriten to also prevent `null` values from ending up as empty params in the query string (`undefined` is already excluded)
export const stringify = (
  params: Record<PropertyKey, unknown>,
  options?: IStringifyOptions
) =>
  qsStringify(
    mapValues(params, (value) => (value === null ? undefined : value)),
    options
  );

// Search
// ------

const mapRooms = (rooms: Array<RoomType>): Record<string, string | number> =>
  rooms.reduce(
    (roomResult, room, roomIndex) => ({
      ...roomResult,
      [`rooms[${roomIndex}][adults]`]: room.adultsCount,
      // eslint-disable-next-line unicorn/prefer-object-from-entries -- ...
      ...room.childrenAges.reduce(
        (childrenResult, childrenAge, childrenIndex) => ({
          ...childrenResult,
          [`rooms[${roomIndex}][children][${childrenIndex}]`]: childrenAge,
        }),
        {}
      ),
      // eslint-disable-next-line unicorn/prefer-object-from-entries -- ...
      ...room.roomTypes.reduce(
        (roomTypesResult, roomType, roomTypeIndex) => ({
          ...roomTypesResult,
          [`rooms[${roomIndex}][roomType][${roomTypeIndex}]`]: roomType,
        }),
        {}
      ),
      // eslint-disable-next-line unicorn/prefer-object-from-entries -- ...
      ...room.boardTypes.reduce(
        (boardTypesResult, boardType, boardTypeIndex) => ({
          ...boardTypesResult,
          [`rooms[${roomIndex}][boardType][${boardTypeIndex}]`]: boardType,
        }),
        {}
      ),
    }),
    {}
  );

export const convertSearchParamsToBackend = ({
  searchParams,
  globalRoomTypesFilter,
  globalBoardTypesFilter,
}: {
  searchParams: SearchParams;
  globalRoomTypesFilter?: Array<string> | null;
  globalBoardTypesFilter?: Array<string> | null;
}): SearchParamsBackend => {
  const searchParamsBackend: SearchParamsBackend = {
    destinationId: searchParams.destinationId || '',
    dateMin: searchParams.dateMin,
    dateMax: searchParams.dateMax,
    airports: searchParams.airports,
    durationMin: searchParams.durationMin,
    durationMax: searchParams.durationMax,
    travelType: searchParams.travelType,
    ...mapRooms(
      Array.from(searchParams.rooms ?? [], (room) => ({
        ...room,
        // @FIXME
        // Existing global room/board type filter parameters need to be set for all rooms.
        // In the future it might be possible though to set them individually for each room.
        roomTypes:
          room.roomTypes.length > 0
            ? room.roomTypes
            : (globalRoomTypesFilter ?? []),
        boardTypes:
          room.boardTypes.length > 0
            ? room.boardTypes
            : (globalBoardTypesFilter ?? []),
      }))
    ),
  };

  // remove some params that don't make sense for own arrival,
  // otherwise we might get unexpected results.
  if (searchParamsBackend.travelType === TravelTypes.ownArrival) {
    searchParamsBackend.airports = null;
  }

  if (
    searchParamsBackend.dateMin &&
    searchParamsBackend.dateMax &&
    searchParamsBackend.durationMin === 0 &&
    searchParamsBackend.durationMax === 0
  ) {
    const dateMin = parseISO(searchParamsBackend.dateMin);
    const dateMax = parseISO(searchParamsBackend.dateMax);
    const duration = differenceInDays(dateMax, dateMin);

    if (duration >= 1) {
      searchParamsBackend.durationMin = duration;
      searchParamsBackend.durationMax = duration;
    }
  }

  return searchParamsBackend;
};

export const convertSearchParamsBackendToParamsString = (
  searchParamsBackend: SearchParamsBackend
): string => {
  return stringify(searchParamsBackend, {
    arrayFormat: 'comma',
  });
};

//  Filter
// -------

const convertFilterParamsToBackend = (
  filterParams: (FilterParamsHotels | FilterParamsOffers) | null
): FilterParamsBackend => {
  if (filterParams === null) {
    return {};
  }

  const filterParamsBackend: FilterParamsBackend = {
    categoryMin:
      'categoryMin' in filterParams ? filterParams.categoryMin : null,
    durationFilter: 'duration' in filterParams ? filterParams.duration : null,
    hotelAttributes:
      'hotelAttributes' in filterParams ? filterParams.hotelAttributes : null,
    roomTypes: filterParams.roomTypes,
    boardTypes: filterParams.boardTypes,
    roomAttributes: filterParams.roomAttributes,
    transportations: filterParams.transportations,
    tourOperators: filterParams.tourOperators,
    specials: filterParams.specials,
    destinationIdFilter: filterParams.destinationIdFilter,
    distanceAirportMax: filterParams.distanceAirportMax,
    distanceBeachMax: filterParams.distanceBeachMax,
    distanceCityCenterMax: filterParams.distanceCityCenterMax,
    distanceSkiSlopeMax: filterParams.distanceSkiSlopeMax,
  };

  if (filterParams.priceMax != null) {
    // lowest accepted value in BFF is 1 instead of 0
    filterParamsBackend.priceMax =
      filterParams.priceMax.amount > 0 ? filterParams.priceMax.amount : 1;
  }

  if (
    filterParams.priceMin != null &&
    // @FIXME
    // Temporary solution to prevent sending the priceMin filter to the backend when it's disabled.
    // Should be handled once in some layer on top which assembles the request parameters.
    config.filters.price.priceMinEnabled === true
  ) {
    filterParamsBackend.priceMin =
      filterParams.priceMin.amount > 0 ? filterParams.priceMin.amount : 1;
  }

  if ('ratingMin' in filterParams && filterParams.ratingMin) {
    const threshold = getRatingCategoryThreshold(filterParams.ratingMin);

    const value =
      threshold * (REVIEW_SCORE_AMADEUS_MAX_BACKEND / REVIEW_SCORE_AMADEUS_MAX);
    if (value) {
      filterParamsBackend.ratingMin = value;
    }
  }

  if ('departureTimeMin' in filterParams && filterParams.departureTimeMin) {
    filterParamsBackend.departureTimeMin = formatMinutesAsTime(
      filterParams.departureTimeMin
    );
  }

  if ('departureTimeMax' in filterParams && filterParams.departureTimeMax) {
    filterParamsBackend.departureTimeMax = formatMinutesAsTime(
      filterParams.departureTimeMax
    );
  }

  if ('returnTimeMin' in filterParams && filterParams.returnTimeMin) {
    filterParamsBackend.returnTimeMin = formatMinutesAsTime(
      filterParams.returnTimeMin
    );
  }

  if ('returnTimeMax' in filterParams && filterParams.returnTimeMax) {
    filterParamsBackend.returnTimeMax = formatMinutesAsTime(
      filterParams.returnTimeMax
    );
  }

  if (
    'airlines' in filterParams &&
    filterParams.airlines != null &&
    filterParams.airlines.length > 0
  ) {
    filterParamsBackend.airlines = filterParams.airlines;
  }

  if (
    'arrivalAirports' in filterParams &&
    filterParams.arrivalAirports != null &&
    filterParams.arrivalAirports.length > 0
  ) {
    filterParamsBackend.arrivalAirports = filterParams.arrivalAirports;
  }

  return filterParamsBackend;
};

export const convertHotelFilterParamsToBackend = (
  hotelFilterParams: null | FilterParamsHotels
): FilterParamsBackend => {
  return convertFilterParamsToBackend(hotelFilterParams);
};

export const convertOfferFilterParamsToBackend = (
  offerFilterParams: null | FilterParamsOffers
): FilterParamsBackend => {
  return convertFilterParamsToBackend(offerFilterParams);
};

export const convertFilterParamsBackendToParamsString = (
  filterParamsBackend: FilterParamsBackend
): string => {
  return stringify(filterParamsBackend, {
    arrayFormat: 'comma',
  });
};

//  Sort
// -----

export const convertSortParamsToBackend = (
  sortParams: SortParams
): SortParamsBackend => {
  return {
    sortBy: sortParams.sortBy,
  };
};

export const convertSortParamsBackendToParamsString = (
  sortParamsBackend: SortParamsBackend
): string => stringify(sortParamsBackend);

// Other
// -----

export const convertOtherParamsBackendToParamsString = (params: {
  offset?: number;
  limit?: number | null;
}): string => stringify({...params, ratingType: BFF_RATING_TYPE_AMADEUS});
